import type { DraftChangeResponse } from '../api/models/response/modificationRequest'
import type { FileAttachmentResponse } from '../api/models/response/proveedor'

/**
 * Helpers de la Solicitud de Modificación para filas "stageadas" (altas aún no aplicadas).
 * El overlay del wizard les da id `staged-<draftChangeId>`; el id que devuelve el alta
 * optimista es `staged-<timestamp>` hasta el siguiente load().
 */

const PREFIX = 'staged-'

const parse = (json: string): Record<string, unknown> | null => {
  try { return JSON.parse(json) as Record<string, unknown> } catch { return null }
}

/**
 * Alta stageada a la que corresponde la fila. Antes se tomaba la PRIMERA alta del tipo, así
 * que quitar el segundo contacto (o cuenta) stageado borraba el primero.
 */
export function findStagedInsert(
  changes: DraftChangeResponse[], entityName: string, rowId: string,
): DraftChangeResponse | undefined {
  const inserts = changes.filter(c => c.entityName === entityName && c.operation === 0)
  const changeId = rowId.startsWith(PREFIX) ? rowId.substring(PREFIX.length) : rowId
  // Id optimista (`staged-<timestamp>`): aún no se recargó; la fila es la última alta.
  return inserts.find(c => c.id === changeId) ?? inserts[inserts.length - 1]
}

/** Payload del alta stageada `staged-<changeId>`, o null si la fila no está stageada. */
export function stagedPayload(changes: DraftChangeResponse[], rowId: string): Record<string, unknown> | null {
  if (!rowId.startsWith(PREFIX)) return null
  const change = changes.find(c => c.id === rowId.substring(PREFIX.length))
  return change ? parse(change.proposedSnapshotJson) : null
}

/**
 * Editar una fila stageada la reemplaza por un alta nueva. Sin esto, si el usuario no volvía
 * a subir el archivo, el adjunto ya stageado (PDF del IGAFOM, certificado del accionista…)
 * se perdía. Devuelve el adjunto nuevo o, si no hay, el del alta anterior.
 */
export function keepStagedValue<T>(fresh: T | null | undefined, previous: Record<string, unknown> | null, key: string): T | null {
  if (fresh !== null && fresh !== undefined) return fresh
  const prev = previous?.[key]
  return prev === undefined || prev === null ? null : (prev as T)
}

export type StagedCompanyFields = Partial<Record<'commercialName' | 'taxAddress' | 'ubigeoCode' | 'mainPhone' | 'mainEmail' | 'website', string>>

/**
 * Datos de empresa ya stageados (cambio Company). El formulario de Identidad arranca con
 * ellos: si arrancaba con los originales, el siguiente "Guardar cambios" borraba del cambio
 * todo campo que coincidiera con el original y se perdía lo stageado antes.
 */
export function extractStagedCompany(changes: DraftChangeResponse[]): StagedCompanyFields {
  const change = changes.find(c => c.entityName === 'Company' && c.operation === 1)
  return (change ? parse(change.proposedSnapshotJson) : null) as StagedCompanyFields | null ?? {}
}

const STAGED_FILE_PREFIX = 'staged:'

/**
 * Adjunto subido en una solicitud de modificación (aún en "_staging", sin ruta definitiva).
 * Las tarjetas mostraban "Sin archivo" para un registro agregado o editado en la solicitud
 * aunque el PDF estuviera cargado. La ruta "staged:<solicitud>/<archivo>" la entiende
 * DocumentoDescarga y la baja por el endpoint de archivos de la solicitud.
 */
export function stagedFileRef(
  modificationRequestId: string, fileAttachmentId: string | null | undefined, fileName?: string | null,
): FileAttachmentResponse | null {
  if (!fileAttachmentId) return null
  return {
    id: fileAttachmentId,
    originalFileName: fileName ?? 'archivo-de-la-solicitud.pdf',
    storagePath: `${STAGED_FILE_PREFIX}${modificationRequestId}/${fileAttachmentId}`,
    contentType: 'application/pdf',
    extension: null,
    fileSize: 0,
    uploadedAt: '',
  }
}

/** [solicitud, archivo] de una ruta "staged:…", o null si es una ruta normal del bucket. */
export function parseStagedFileRef(storagePath: string | null | undefined): [string, string] | null {
  if (!storagePath?.startsWith(STAGED_FILE_PREFIX)) return null
  const [mrId, fileId] = storagePath.substring(STAGED_FILE_PREFIX.length).split('/')
  return mrId && fileId ? [mrId, fileId] : null
}

/**
 * Vigencias de poder stageadas que dependen de un representante: las que referencian su
 * alta stageada (companyPersonDraftChangeId) o su vínculo existente (companyPersonId).
 * Al quitar o dar de baja al representante deben retirarse también: si no, la aprobación
 * abortaba ("…que esta solicitud ya no agrega") o insertaba una vigencia huérfana.
 */
export function dependentPowerValidities(
  changes: DraftChangeResponse[], ref: { draftChangeId?: string; companyPersonId?: string },
): DraftChangeResponse[] {
  return changes.filter(c => {
    if (c.entityName !== 'PowerValidity' || c.operation !== 0) return false
    const p = parse(c.proposedSnapshotJson)
    return (ref.draftChangeId != null && p?.companyPersonDraftChangeId === ref.draftChangeId)
      || (ref.companyPersonId != null && p?.companyPersonId === ref.companyPersonId)
  })
}
